import type { P8OverviewKpi } from "../../p8OverviewData";
import { useIsCompact, useStackGapClass } from "../CardDensity";

import { DottedLabel } from "./DottedLabel";
import { HeaderTooltip } from "./HeaderTooltip";

type OverviewKpiGridProps = {
  items: P8OverviewKpi[];
  /**
   * Column count at the widest breakpoint. Five keeps a five-metric row on one
   * line instead of wrapping the last card onto a row of its own; the smaller
   * counts let a short set spread across the card rather than huddling at its
   * left edge.
   */
  columns?: 2 | 3 | 4 | 5;
};

const WIDE_COLUMNS_CLASS: Record<2 | 3 | 4 | 5, string> = {
  2: "min-[1280px]:grid-cols-2",
  3: "min-[1280px]:grid-cols-3",
  4: "min-[1280px]:grid-cols-4",
  5: "min-[1280px]:grid-cols-5",
};

export function OverviewKpiGrid({ items, columns = 4 }: OverviewKpiGridProps) {
  const isCompact = useIsCompact();
  const gapClass = useStackGapClass();

  return (
    <ul
      className={`grid grid-cols-1 ${gapClass} min-[750px]:grid-cols-2 ${WIDE_COLUMNS_CLASS[columns]}`}
    >
      {items.map((item) => (
        <li key={item.title} className="text-sm leading-5">
          {item.tooltip ? (
            <HeaderTooltip
              tooltip={item.tooltip}
              interactive={item.interactiveTooltip}
            >
              <DottedLabel>{item.title}</DottedLabel>
            </HeaderTooltip>
          ) : (
            <DottedLabel>{item.title}</DottedLabel>
          )}
          <p className="mt-1 text-[30px] leading-[38px] font-normal text-[#333]">
            {item.value}
          </p>
          <div className={isCompact ? "mt-1 space-y-0.5" : "mt-2 space-y-1"}>
            {item.footers.map((footer) => (
              <p key={footer} className="text-xs leading-4 text-[#757575]">
                {footer}
              </p>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}
